import { UploadObjectType } from 'app/types/enums/uploadObjectType';
import { sanitizeFileName } from '@helpers/sanitizeFileName';
import { MediaService, RepositoriesService } from 'app/API';
import type { TUploaderAdapter, TUploadParams } from '../types/TUploadParams';
import type { TCall } from 'app/queries/types/TCall';

export async function fetchUploadParams(
  metaName: string,
  objectId: string,
  objectType: UploadObjectType,
  call: TCall,
): Promise<TUploadParams> {
  const fileName = sanitizeFileName(metaName);
  const paramsFileName = encodeURIComponent(fileName);
  if (!objectId) {
    throw new Error('Wrong Job ID');
  }
  let resp;
  if (objectType === UploadObjectType.REPOSITORY) {
    resp = await call(() => RepositoriesService.generateUploadParams({ repositoryId: objectId, file: paramsFileName }));
  } else {
    resp = await call(() => MediaService.generateUploadParams({ jobId: objectId, file: paramsFileName }));
  }

  const { fields, url } = resp;

  return {
    fields,
    method: 'PUT',
    meta: {
      fileUrl: `https://${fields.bucket}.s3.amazonaws.com/${fields.key}`,
    },
    url,
  };
}
export async function putToS3(arg: {
  file: File;
  params: TUploadParams;
  onProgress?: (p: number) => void;
  signal?: AbortSignal;
}) {
  const { file, params, onProgress, signal } = arg;
  if (params.method !== 'PUT') {
    throw new Error('Expected PUT signed URL');
  }
  return new Promise<{ serverId?: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', params.url);
    if (params.headers) {
      Object.entries(params.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress((e.loaded / e.total) * 100);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({});
        return;
      }
      reject(new Error(`S3 PUT failed ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(file);
  });
}

export const S3SignedUrlUploader = (call: TCall): TUploaderAdapter => {
  return {
    requestParams: async ({ file, meta }) => {
      if (!(file instanceof File)) {
        throw new Error('Only File supported');
      }
      return fetchUploadParams(meta.name, meta.extra?.jobId, UploadObjectType.JOB, call);
    },
    performUpload: async ({ file, params, onProgress, signal }) => {
      if (!(file instanceof File)) {
        throw new Error('Only File supported');
      }
      return putToS3({ file, params, onProgress, signal });
    },
  };
};
